import { zodResolver } from '@hookform/resolvers/zod';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import { AccountUpdateDtoSchema } from '_lib/api/apiTypes.shim';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Card } from '_lib/components/Card/Card';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { useIsOverflowing } from '_lib/hooks/useIsOverflowing';

import { extractAllErrors } from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';

import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { AccountUserTabs } from '../AccountUser/AccountUserTabs';
import { AccountCompanyTabs } from '../Company/AccountCompanyTabs';
import { NavigationBlocker } from '../NavigationBlocker/NavigationBlocker';
import { RouteErrorBoundary } from '../Routing/RouteErrorBoundary';

import { AccountForm } from './AccountForm';
import { useReactivateAccountDialog } from './hooks/ReactivateAccountDialogHook';

type AccountUpdateProps = {
  accountId: string;
};

export const AccountUpdate = (props: AccountUpdateProps) => {
  const { accountId } = props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const { enqueueSnackbar } = useOurSnackbar();

  const scrollTriggerTarget = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useIsOverflowing(scrollTriggerTarget);

  const confirmationDialog = useConfirmationDialog();
  const reactivateAccountDialog = useReactivateAccountDialog();

  const api = useContext(ApiContext);

  const accountPutMutation = api.accounts.usePut();
  const accountDeleteMutation = api.accounts.useDelete();

  const accountQuery = api.accounts.useGetById({ id: accountId });
  const accountData = accountQuery?.data;

  const accountFormContext = useForm({
    resolver: zodResolver(AccountUpdateDtoSchema.required()),
    mode: 'onChange',
    shouldFocusError: true
  });

  useEffect(() => {
    if (accountData) {
      accountFormContext.reset(accountData);
    }
  }, [accountData, accountFormContext]);

  const handleSave = apiSubmitHandler(
    accountFormContext.handleSubmit,
    accountFormContext.setError,
    async (data, { setFormErrors }) => {
      setNonFieldErrors(undefined);
      setAllErrors(undefined);
      try {
        const result = await accountPutMutation.put({ id: accountId, data });
        enqueueSnackbar('Account saved');
        accountFormContext.reset(result);
      } catch (err) {
        if (err instanceof BadRequestError) {
          setNonFieldErrors(err.responseData?.nonFieldErrors);
          setFormErrors(err);
        }
      }
    },
    {
      onInvalid: (errors) => {
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors, allErrors);
      }
    }
  );

  const handleDeactivate = async () => {
    try {
      const confirmDeactivate = await confirmationDialog.show({
        titleText: 'Deactivate Account?',
        contentText: 'All account companies will be also be deactivated.'
      });

      if (confirmDeactivate?.action !== 'CONFIRM') return;

      await accountDeleteMutation.deleteItem({ id: accountId });
      enqueueSnackbar('Account deactivated');
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
      }
    }
  };

  const handleReactivate = async () => {
    try {
      if (!accountData) return;

      const reactivateResult = await reactivateAccountDialog.show({ account: accountData });
      if (reactivateResult?.action !== 'CONFIRM') return;

      enqueueSnackbar('Account reactivated');
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
      }
    }
  };

  if (accountQuery.error) {
    return <RouteErrorBoundary disableAppBar errorTitle='Error Fetching Account' />;
  }

  const isFetchingOrPending =
    accountQuery.isFetching || accountPutMutation.isPending || accountDeleteMutation.isPending;

  if (!isFetchingOrPending && !accountData) {
    return <RouteErrorBoundary errorTitle='Account Not Found' />;
  }

  return (
    <>
      <NavigationBlocker getBlockCondition={() => Object.keys(accountFormContext.formState.dirtyFields).length > 0} />
      <ActionBar
        actions={
          <Button
            disableFocusRipple
            text={accountData?.isActive ? 'Deactivate' : 'Reactivate'}
            variant='text'
            onClick={accountData?.isActive ? handleDeactivate : handleReactivate}
            startIcon={accountData?.isActive ? <DeleteIcon /> : <SettingsBackupRestoreIcon />}
            sx={{ textTransform: 'none' }}
            disabled={isFetchingOrPending}
          />
        }
      />
      <Divider />
      <FlexBox
        sx={(theme) => ({
          backgroundColor: theme.palette.background.default,
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          position: 'relative'
        })}>
        <BackdropSpinner open={isFetchingOrPending} />
        {!!Object.keys(accountFormContext.getValues()).length && (
          <>
            <Stack padding={2} useFlexGap spacing={2} overflow='auto' ref={scrollTriggerTarget}>
              {!!nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1 }} />}
              {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
              {!accountQuery.isFetching && accountData?.isActive === false && (
                <WarningAlert>
                  <Typography>Account is inactive</Typography>
                </WarningAlert>
              )}
              <AccountForm accountFormContext={accountFormContext} isInactive={!accountData?.isActive} />
              {!accountQuery.isFetching &&
                !!accountData &&
                accountData.isActive &&
                (accountData.companies === null ||
                  accountData.companies?.length === 0 ||
                  accountData.companies?.every((company) => company.isActive === false)) && (
                  <WarningAlert>
                    <Typography>
                      Account Users will only be able to login and access Portal if it is enabled and at least one
                      company is active for the Account.
                    </Typography>
                  </WarningAlert>
                )}
              <Card sx={{ display: 'flex', flexDirection: 'column', minHeight: '208px' }}>
                <PrimaryCardHeader title='Companies' />
                {!!accountData && <AccountCompanyTabs account={accountData} />}
              </Card>
              <Card sx={{ display: 'flex', flexDirection: 'column', minHeight: '208px' }}>
                <PrimaryCardHeader title='Users' />
                <AccountUserTabs accountId={accountId} account={accountData} sx={{ maxHeight: '400px' }} />
              </Card>
            </Stack>
            {isOverflowing && <Divider />}
            <FlexBox sx={{ paddingX: 2, paddingY: 1, marginLeft: 'auto' }}>
              <Button
                disabled={!accountData?.isActive}
                onClick={handleSave}
                text='Save'
                variant='contained'
                size='small'
                sx={{ textTransform: 'none' }}
              />
            </FlexBox>
          </>
        )}
      </FlexBox>
      {confirmationDialog.element}
      {reactivateAccountDialog.element}
    </>
  );
};
